import * as React from 'react';
import axios from 'axios';
import {
  Avatar,
  Box,
  Link,
  IconButton,
  Button,
  Drawer,
  InputBase,
  CircularProgress,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '../Components/AppBar';
import Toolbar from '../Components/Toolbar';
import { debounce } from 'lodash';

// Updated Document type to include matter
type Document = {
  id: string;
  filename: string;
  description?: string;
  isPublic?: boolean;
  legalMatter?: {
    id: string;
    name: string;
  };
  [key: string]: any;
};

const rightLink = {
  fontSize: 16,
  color: 'common.white',
  ml: 3,
};

const pages = [
  { label: 'Home', href: '/' },
  { label: 'Features', href: '/#features' },
  { label: 'How It Works', href: '/#howitworks' },
  { label: 'Services', href: '/#prodcategories' },
  { label: 'Contact Us', href: '/#contactus' },
];

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

export const AppNavBar: React.FC = () => {
  const [anchorElNav, setAnchorElNav] = React.useState<false | HTMLElement>(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<Document[]>([]);
  const [loading, setLoading] = React.useState(false);

  const handleOpenHamburgerNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenus = () => setAnchorElNav(false);

  // Debounced search for public documents
  const performSearch = React.useCallback(
    debounce(async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setLoading(false);
        return;
      }
  
      setLoading(true);
      try {
        const res = await axios.get('https://demo.insollex.com/api/public-documents', {
          params: { query: query.trim() },
        });
  
        console.log('Search API raw response:', res.data);
  
        const data: any = res.data;
  
        // Normalize results and ensure fileUrl exists
        const docsNormalized = data.map((d: any) => ({
          id: d.id,
          filename: d.filename || d.title || 'Untitled',
          description: d.description || '',
          isPublic: !!d.isPublic,
          legalMatter: d.legalMatter
            ? { id: d.legalMatter.id, name: d.legalMatter.name || d.legalMatter.matterName }
            : undefined,
          // Direct public URL to the file for browser PDF viewing
          fileUrl: d.fileUrl || `https://demo.insollex.com/files/${encodeURIComponent(d.filename || d.title)}`,
        }));
  
        console.log('Search normalized results:', docsNormalized);
        setSearchResults(docsNormalized);
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  const downloadFile = async (filename: string) => {
    try {
      const res = await fetch(`/api/download/${encodeURIComponent(filename)}`);
  
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Download failed: ${res.status} ${res.statusText} - ${text}`);
      }
  
      const blob = await res.blob();
  
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.replace(/[^a-z0-9_\-\.]/gi, '_');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
  
      console.log('File downloaded successfully:', filename);
    } catch (err) {
      console.error('Download error:', err);
    }
  };
  
  
  
  
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    performSearch(value);
  };

  const handleSelectResult = (doc: Document) => {
    window.location.href = `/documents/${doc.id}`;
  };

  return (
    <div>
      <AppBar position="fixed">
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Hamburger */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton size="large" aria-label="menu" onClick={handleOpenHamburgerNavMenu} color="inherit">
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Logo */}
          <Avatar alt="Logo" src="/logo/init_logo.png" sx={{ mr: 2 }} />
          <Link variant="h1" underline="none" color="inherit" href="/" sx={{ fontSize: 24 }}>
            My Core Application
          </Link>

          {/* Pages */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {pages.map((page) => (
              <Button key={page.label} href={page.href} sx={{ color: 'white' }}>
                {page.label}
              </Button>
            ))}
          </Box>

          {/* Drawer */}
          <Drawer
            anchor="left"
            keepMounted
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenus}
            sx={{ display: { xs: 'block', md: 'none' } }}
          >
            {pages.map((page) => (
              <Button key={page.label} href={page.href} onClick={handleCloseNavMenus}>
                {page.label}
              </Button>
            ))}
          </Drawer>

         {/* Search Bar */}
<Box sx={{ position: 'relative', ml: 2, flex: 1, maxWidth: 400 }}>
  <Search sx={{ width: '100%' }}>
    <SearchIconWrapper>
      <SearchIcon />
    </SearchIconWrapper>
    <StyledInputBase
      placeholder="Search Documents or Matters..."
      value={searchQuery}
      onChange={handleInputChange}
      inputProps={{ 'aria-label': 'search' }}
      sx={{ width: '100%', fontSize: 16, padding: '8px 8px 8px 40px' }}
    />
  </Search>

  {/* Dropdown */}
  {searchQuery && (
    <Box
      sx={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: 'white',
        color: 'black',
        zIndex: 1000,
        maxHeight: 500,
        overflowY: 'auto',
        boxShadow: 4,
        borderRadius: 1,
      }}
    >
      {loading ? (
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <List>
          {searchResults.length > 0 ? (
            <>
              {/* Documents Section */}
              {searchResults.filter(d => d.filename).length > 0 && (
                <Box sx={{ p: 1, bgcolor: 'grey.100' }}>
                  <Typography variant="subtitle2">Documents</Typography>
                </Box>
              )}
              {searchResults
                .filter(d => d.filename)
                .map((doc) => (
                  <ListItem key={doc.id} button>
                    <Box>
                      {/* Use the public download function */}
                      <Link
                        href="#"
                        underline="hover"
                        sx={{ fontWeight: 500, fontSize: 16 }}
                        onClick={(e) => {
                          e.preventDefault();
                          downloadFile(doc.filename); // No token required
                        }}
                      >
                        {doc.filename}
                      </Link>

                      {doc.description && (
                        <Typography variant="body2" color="text.secondary">
                          {doc.description}
                        </Typography>
                      )}
                      {doc.legalMatter?.name && (
                        <Typography variant="caption" color="text.secondary">
                          Matter: {doc.legalMatter.name}
                        </Typography>
                      )}
                    </Box>
                  </ListItem>
                ))}

              {/* Matters Section */}
              {searchResults.filter(d => !d.filename && d.legalMatter?.name).length > 0 && (
                <Box sx={{ p: 1, bgcolor: 'grey.100' }}>
                  <Typography variant="subtitle2">Matters</Typography>
                </Box>
              )}
              {searchResults
                .filter(d => !d.filename && d.legalMatter?.name)
                .map((matter) => (
                  <ListItem
                    key={matter.legalMatter?.id || matter.id}
                    button
                    onClick={() => handleSelectResult(matter)}
                  >
                    <Box>
                      <Typography variant="body1">{matter.legalMatter?.name}</Typography>
                    </Box>
                  </ListItem>
                ))}
            </>
          ) : (
            <Box sx={{ p: 3, color: 'text.secondary' }}>No results found.</Box>
          )}
        </List>
      )}
    </Box>
  )}
</Box>



          {/* Sign In/Up */}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Link href="/signin" color="inherit" underline="none" sx={rightLink}>
              Sign In
            </Link>
            <Link href="/signup" underline="none" sx={{ ...rightLink, color: 'secondary.main' }}>
              Sign Up
            </Link>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Spacer */}
      <Box sx={{ height: '64px' }} />
    </div>
  );
};
